import React, { useState, useEffect } from 'react';
import { getQuotations, createQuotation, deleteQuotation, updateQuotationStatus, getCustomers } from '../lib/api';
import { formatCurrency, CURRENCY_SYMBOL } from '../lib/currency';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ExportImportButtons } from '../components/ExportImportButtons';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, MoreVertical, Trash2, MapPin, Calendar, Users, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];

const getStatusClass = (status) => {
  const classes = {
    draft: 'bg-slate-100 text-slate-700',
    sent: 'bg-blue-50 text-blue-700',
    accepted: 'bg-emerald-50 text-emerald-700',
    rejected: 'bg-red-50 text-red-700',
  };
  return classes[status] || 'bg-slate-100 text-slate-700';
};

export const Quotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    destination: '',
    travel_date: '',
    return_date: '',
    travelers: 1,
    package_details: '',
    flight_cost: 0,
    hotel_cost: 0,
    visa_cost: 0,
    other_cost: 0,
    markup_percent: 10,
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [quotationsRes, customersRes] = await Promise.all([
        getQuotations(),
        getCustomers(),
      ]);
      setQuotations(quotationsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      destination: '',
      travel_date: '',
      return_date: '',
      travelers: 1,
      package_details: '',
      flight_cost: 0,
      hotel_cost: 0,
      visa_cost: 0,
      other_cost: 0,
      markup_percent: 10,
      notes: '',
    });
  };

  const calculateTotal = () => {
    const subtotal = parseFloat(formData.flight_cost || 0) + 
                     parseFloat(formData.hotel_cost || 0) + 
                     parseFloat(formData.visa_cost || 0) + 
                     parseFloat(formData.other_cost || 0);
    const markup = subtotal * (parseFloat(formData.markup_percent || 0) / 100);
    return { subtotal, markup, total: subtotal + markup };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await createQuotation({
        ...formData,
        flight_cost: parseFloat(formData.flight_cost) || 0,
        hotel_cost: parseFloat(formData.hotel_cost) || 0,
        visa_cost: parseFloat(formData.visa_cost) || 0,
        other_cost: parseFloat(formData.other_cost) || 0,
        markup_percent: parseFloat(formData.markup_percent) || 0,
        travelers: parseInt(formData.travelers) || 1,
      });
      toast.success('Quotation created successfully');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quotation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this quotation?')) return;

    try {
      await deleteQuotation(id);
      toast.success('Quotation deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete quotation');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateQuotationStatus(id, newStatus);
      toast.success('Status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Unknown';
  };

  const { subtotal, markup, total } = calculateTotal();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight" style={{ color: '#84bd0e' }}>Quotation Management</h1>
          <p className="text-muted-foreground mt-1">Create and manage travel quotations</p>
        </div>
        <div className="flex items-center gap-3">
          <ExportImportButtons module="quotations" onImportSuccess={loadData} />
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />
              New Quotation
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-outfit">Create Quotation</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Customer *</Label>
                <Select value={formData.customer_id} onValueChange={(v) => setFormData({ ...formData, customer_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Destination *</Label>
                  <Input
                    value={formData.destination}
                    onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                    placeholder="e.g., Maldives"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Travelers</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.travelers}
                    onChange={(e) => setFormData({ ...formData, travelers: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Travel Date *</Label>
                  <Input
                    type="date"
                    value={formData.travel_date}
                    onChange={(e) => setFormData({ ...formData, travel_date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Return Date *</Label>
                  <Input
                    type="date"
                    value={formData.return_date}
                    onChange={(e) => setFormData({ ...formData, return_date: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Package Details *</Label>
                <Textarea
                  value={formData.package_details}
                  onChange={(e) => setFormData({ ...formData, package_details: e.target.value })}
                  placeholder="Describe the travel package..."
                  rows={3}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Flight Cost ({CURRENCY_SYMBOL})</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.flight_cost}
                    onChange={(e) => setFormData({ ...formData, flight_cost: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hotel Cost ({CURRENCY_SYMBOL})</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.hotel_cost}
                    onChange={(e) => setFormData({ ...formData, hotel_cost: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Visa Cost ({CURRENCY_SYMBOL})</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.visa_cost}
                    onChange={(e) => setFormData({ ...formData, visa_cost: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Other Costs ({CURRENCY_SYMBOL})</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.other_cost}
                    onChange={(e) => setFormData({ ...formData, other_cost: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Markup (%)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.markup_percent}
                  onChange={(e) => setFormData({ ...formData, markup_percent: e.target.value })}
                />
              </div>

              {/* Price Summary */}
              <Card className="bg-slate-50 border-slate-200">
                <CardContent className="p-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Subtotal:</span>
                      <span className="font-medium">{formatCurrency(subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Markup ({formData.markup_percent}%):</span>
                      <span className="font-medium">{formatCurrency(markup)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-slate-200">
                      <span className="font-semibold text-slate-900">Total:</span>
                      <span className="font-bold text-slate-900">{formatCurrency(total)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={submitting}>
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Create Quotation
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Quotations Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Customer</TableHead>
                <TableHead className="font-semibold">Destination</TableHead>
                <TableHead className="font-semibold">Dates</TableHead>
                <TableHead className="font-semibold">Travelers</TableHead>
                <TableHead className="font-semibold text-right">Total</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotations.length > 0 ? (
                quotations.map((q) => (
                  <TableRow key={q.id} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{getCustomerName(q.customer_id)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-600">
                        <MapPin className="w-3 h-3" />
                        {q.destination}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Calendar className="w-3 h-3" />
                        {q.travel_date} - {q.return_date}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Users className="w-3 h-3" />
                        {q.travelers}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(q.total)}</TableCell>
                    <TableCell>
                      <Select value={q.status} onValueChange={(v) => handleStatusChange(q.id, v)}>
                        <SelectTrigger className={`w-28 h-8 text-xs ${getStatusClass(q.status)}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/quotations/${q.id}/pdf`, '_blank')}>
                            <FileText className="w-4 h-4 mr-2" />
                            Download PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(q.id)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No quotations found. Click "New Quotation" to create one.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
